import React, { useEffect, useState, useCallback } from "react";
import { t } from "i18next";
import i18n from "i18n";
import { UserAgreementForm } from "interface/userAgreement";
import { CustomInput } from "ui/Input";
import CustomButton, { TextButton } from "ui/Button";
import { COLOR, ICON_NAMES } from "utils/constant";
import styles from "styles.module.css";
import { Languages } from "lucide-react";
import CustomModal from "ui/modal/Modal";
import {
	getCatchSignerUserApi,
	registerUserAgreementApi,
} from "api/userAgreement";
import {
	CustomerTypeKey,
	defaultForm,
	computeClearedFields,
} from "./utilities/helper";
import "./userAgreement.css";
import CustomDropDownList from "ui/dropDown";
import {
	getBuyersFilterApi,
	getGetInternationalCountryCodeApi,
	getTerminalsApi,
} from "api/common";
import { DropdownRenderItem } from "ui/dropdownItemList";
import { InternationalCountryCode } from "interface/common";
import { ListItemProps } from "@progress/kendo-react-dropdowns";

const UserAgreementPage: React.FC = () => {
	const [form, setForm] = useState<UserAgreementForm | null>({
		...defaultForm,
	});
	const [errors, setErrors] = useState<{ [key: string]: string }>({});
	const [currentLanguage, setCurrentLanguage] = useState<string>(i18n.language);
	const [termsModalOpen, setTermsModalOpen] = useState<boolean>(false);
	const [showError, setShowError] = useState<boolean>(false);
	const [successModalOpen, setSuccessModalOpen] = useState<boolean>(false);
	const [countryCodes, setCountryCodes] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [countryCodesFilter, setCountryCodesFilter] = React.useState<
		InternationalCountryCode[]
	>([]);
	const [isMobileView, setIsMobileView] = useState(
		typeof window !== "undefined" ? window.innerWidth < 768 : false,
	);
	const [tempUsername, setTempUsername] = useState<string>("");
	const [usernameValid, setUsernameValid] = useState<boolean>(false);

	const isValidEmail = (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());
	};

	const validateForm = useCallback(() => {
		const newErrors: { [key: string]: string } = {};
		if (!form) {
			setErrors({
				accessType:
					t("AgreementAccessType") + " " + t("Is") + " " + t("Required"),
			});
			return false;
		}
		if (!form.accessType) {
			newErrors.accessType =
				t("AgreementAccessType") + " " + t("Is") + " " + t("Required");
		}
		if (form.termsOfUse === false) {
			newErrors.termsOfUse = t("TermsOfUse") + " " + t("MustBeAccepted");
		}
		if (!form.userFirstName.trim())
			newErrors.userFirstName = t("Missing") + " " + t("FirstName");
		if (!form.userLastName.trim())
			newErrors.userLastName = t("Missing") + " " + t("LastName");
		if (!form.userPhonePrefix || !form.userPhonePrefix.toString().trim())
			newErrors.userPhonePrefix = t("Missing") + " " + t("CountryCode");
		if (!form.userPhone.trim())
			newErrors.userPhone = t("Missing") + " " + t("Phone");
		if (!form.userEmail.trim())
			newErrors.userEmail = t("Missing") + " " + t("Email");
		else if (!isValidEmail(form.userEmail))
			newErrors.userEmail = t("InvalidEmail");

		const noneSelected =
			!form.customerFisher &&
			!form.customerBuyer &&
			!form.customerStorage &&
			!form.customerSales;
		if (noneSelected) newErrors.customerType = t("SelectAtLeastOneOption");

		if ((form.customerFisher || form.customerSales) && !form.vesselName?.trim())
			newErrors.vesselName = t("Missing") + " " + t("VesselName");
		if (
			(form.customerFisher || form.customerSales) &&
			!form.vesselRegNo?.trim()
		)
			newErrors.vesselRegNo = t("Missing") + " " + t("VesselRegistrationNo");

		if ((form.customerBuyer || form.customerSales) && !form.buyerName?.trim())
			newErrors.buyerName = t("Missing") + " " + t("Buyer");
		if (
			(form.customerBuyer || form.customerSales) &&
			(!form.buyerRegNo || Number(form.buyerRegNo) <= 0)
		)
			newErrors.buyerRegNo = t("Missing") + " " + t("Buyer") + " RegNo";

		if (form.customerStorage && !form.terminalName?.trim())
			newErrors.terminalName = t("Missing") + " " + t("Terminal");
		if (form.customerStorage && !form.terminalCode?.trim())
			newErrors.terminalCode = t("Missing") + " " + t("Terminal") + " Code";

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	}, [form]);

	useEffect(() => {
		getGetInternationalCountryCodeApi()
			.then((response) => {
				setCountryCodes(response);
			})
			.catch((error) => {
				console.log(error);
			});
	}, [form, showError, validateForm]);

	useEffect(() => {
		if (showError) validateForm();
	}, [form, showError, validateForm]);

	useEffect(() => {
		const handleResize = () => {
			setIsMobileView(window.innerWidth < 768);
		};

		handleResize();
		window.addEventListener("resize", handleResize);

		return () => {
			window.removeEventListener("resize", handleResize);
		};
	}, []);

	const handleInputChange = (field: keyof UserAgreementForm, value: any) => {
		setForm((prev) => ({ ...(prev ?? defaultForm), [field]: value }));
	};

	const onTypeToggle = (typeKey: CustomerTypeKey, checked: boolean) => {
		setForm((prev) => {
			const cur = prev ?? defaultForm;
			let cleared: Partial<UserAgreementForm> = {};
			if (!checked) {
				cleared = computeClearedFields(typeKey, cur as UserAgreementForm);
			}
			return {
				...(cur as UserAgreementForm),
				[typeKey]: checked,
				...cleared,
			} as UserAgreementForm;
		});
	};

	const handleSubmit = () => {
		const valid = validateForm() && (form?.termsOfUse ?? false);
		if (!valid) {
			setShowError(true);
			return;
		}

		proceedSubmit();
	};

	const proceedSubmit = () => {
		if (!form) {
			setShowError(true);
			return;
		}
		registerUserAgreementApi(form)
			.then((response) => {
				setForm(null);
				setSuccessModalOpen(true);
			})
			.catch((error) => {
				console.error("API error:", error);
			});
		setShowError(false);
	};

	const handleLanguageSwitch = (language: string) => {
		i18n.changeLanguage(language);
		setCurrentLanguage(language);
	};

	const handleFilterChange = (e: string) => {
		const filtered = countryCodes.filter(
			(code) =>
				code.internationalDialing.startsWith(e) ||
				code.country.toLowerCase().startsWith(e.toLowerCase()), //||
		);
		const sorted = filtered.sort((a, b) => a.country.localeCompare(b.country));
		setCountryCodesFilter(sorted);
		// if (filtered.length === 0) {
		// 	setCountryCodesFilter(countryCodes);
		// }
	};

	const isValidUsername = (fieldValue?: string) => {
		if (!fieldValue) {
			return false;
		}

		const normalizedValue = fieldValue.trim().toUpperCase();
		const signaturePattern = /^\d{6}[A-Z]{6}$/;
		return signaturePattern.test(normalizedValue);
	};

	const validateUsername = () => {
		getCatchSignerUserApi(tempUsername)
			.then((response) => {
				if (response.isValid) {
					setUsernameValid(true);
					const signer = response.signUser;
					setForm((prev) => ({
						...(prev ?? defaultForm),
						userFirstName: signer.firstname,
						userLastName: signer.lastname,
						userEmail: signer.email,
						userPhone: signer.phoneNumber,
					}));
				} else {
					console.error("Invalid catch signer user");
					setUsernameValid(false);
					setErrors((prev) => ({
						...prev,
						usernameInValid:
							t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
					}));
				}
			})
			.catch((error) => {
				console.error("Error fetching catch signer user:", error);
				setUsernameValid(false);
				setErrors((prev) => ({
					...prev,
					usernameInValid:
						t("Invalid") + " " + t("CatchSign") + " " + t("Username"),
				}));
			});
	};

	const invalidateUsername = () => {
		setUsernameValid(false);
		setForm((prev) => ({
			...(prev ?? defaultForm),
			userFirstName: "",
			userLastName: "",
			userEmail: "",
			userPhone: "",
		}));
	};
	const Checkbox: React.FC<{
		value: boolean;
		label: string;
		onChange?: (value: boolean) => void;
		showError?: boolean;
	}> = ({ value, label, onChange, showError = false }) => {
		return (
			<label
				style={{
					display: "inline-flex",
					alignItems: "center",
					cursor: "pointer",
				}}
			>
				<input
					type="checkbox"
					checked={value}
					onChange={(e) => onChange && onChange(e.target.checked)}
					style={{
						width: "16px",
						height: "16px",
						marginRight: "8px",
						outline: showError ? "1px solid red" : "1px solid #ccc",
					}}
				/>
				{label}
			</label>
		);
	};

	const RadioButton: React.FC<{
		value: string;
		selectedValue: string | null;
		label: string;
		onChange?: (value: string) => void;
	}> = ({ value, selectedValue, label, onChange }) => {
		return (
			<label
				style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
			>
				<input
					type="radio"
					value={value}
					checked={selectedValue === value}
					onChange={(e) => onChange && onChange(e.target.value)}
					style={{ width: "16px", height: "16px", marginRight: "8px" }}
				/>
				{label}
			</label>
		);
	};

	return (
		<div className={`${styles.PageContainer} user-agreement-page`}>
			<div
				className="user-agreement-language-switch"
				style={{
					display: "flex",
					alignItems: "center",
					flexWrap: isMobileView ? "wrap" : "nowrap",
					gap: "10px",
					justifyContent: "center",
					marginBottom: "20px",
				}}
			>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "no" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "no" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("no")}
				>
					<Languages color={currentLanguage === "no" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "no" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "no" ? "bold" : "normal",
						}}
					>
						Norwegian
					</span>
				</div>
				<div
					className="user-agreement-language-option"
					style={{
						display: "flex",
						alignItems: "center",
						gap: "10px",
						cursor: "pointer",
						padding: "8px 12px",
						borderRadius: "4px",
						backgroundColor:
							currentLanguage === "en" ? "#f0f0f0" : "transparent",
						border:
							currentLanguage === "en" ? "2px solid #007bff" : "1px solid #ccc",
					}}
					onClick={() => handleLanguageSwitch("en")}
				>
					<Languages color={currentLanguage === "en" ? "#007bff" : "#666"} />
					<span
						style={{
							color: currentLanguage === "en" ? "#007bff" : "#666",
							fontWeight: currentLanguage === "en" ? "bold" : "normal",
						}}
					>
						English
					</span>
				</div>
			</div>
			<div className="page-header" style={{ textAlign: "center" }}>
				<legend className={styles.BoxHeadingLarge}>{t("UserAgreement")}</legend>
			</div>

			<div className="user-agreement-content">
				<div className="user-agreement-section">
					<div className={styles.BoxHeadingMedium}>
						{t("AgreementAccessType")}
					</div>
					<div
						className="user-agreement-radio-group"
						style={{
							display: "flex",
							flexDirection: "column",
							gap: "10px",
							marginTop: "15px",
						}}
					>
						<RadioButton
							value={"1"}
							selectedValue={form?.accessType?.toString() || ""}
							label={t("ReplaceAllAccess")}
							onChange={() =>
								setForm((prev) => ({ ...(prev ?? defaultForm), accessType: 1 }))
							}
						/>
						<RadioButton
							value={"2"}
							selectedValue={form?.accessType?.toString() || ""}
							label={t("NewAccessInAddition")}
							onChange={() =>
								setForm((prev) => ({ ...(prev ?? defaultForm), accessType: 2 }))
							}
						/>
						{errors.accessType && (
							<div style={{ color: "red", fontSize: "12px", marginTop: "4px" }}>
								{errors.accessType}
							</div>
						)}
					</div>
				</div>
				<div className="user-agreement-two-column-row">
					<div className="user-agreement-column">
						<div>
							<label
								className="user-agreement-field-label"
								style={{
									fontWeight: "bold",
									marginBottom: "10px",
									display: "block",
								}}
							>
								{t("CustomerType")}
							</label>
							<div
								className="user-agreement-checkbox-group"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "8px",
									alignItems: "flex-start",
									border: errors.customerType ? "1px solid red" : "none",
									padding: errors.customerType ? "8px" : "0",
									borderRadius: errors.customerType ? "4px" : "0",
								}}
							>
								<Checkbox
									value={form?.customerFisher ?? false}
									label={`${t("Fisherman")}`}
									onChange={(value) => onTypeToggle("customerFisher", value)}
								/>
								<Checkbox
									value={form?.customerBuyer ?? false}
									label={t("Buyer")}
									onChange={(value) => onTypeToggle("customerBuyer", value)}
								/>
								<Checkbox
									value={form?.customerStorage ?? false}
									label={t("FreezerStorage")}
									onChange={(value) => onTypeToggle("customerStorage", value)}
								/>
								<Checkbox
									value={form?.customerSales ?? false}
									label={t("DockSale")}
									onChange={(value) => onTypeToggle("customerSales", value)}
								/>
							</div>
							{errors.customerType && (
								<div
									style={{ color: "red", fontSize: "12px", marginTop: "4px" }}
								>
									{errors.customerType}
								</div>
							)}
						</div>
					</div>
					<div className="user-agreement-column user-agreement-column-spacer"></div>
				</div>

				<div className="user-agreement-section">
					<h2
						style={{ marginBottom: "20px" }}
						className={styles.BoxHeadingMedium}
					>
						1. {t("CustomerInformation")}
					</h2>
					<p
						style={{ marginBottom: "20px" }}
						className={styles.BoxHeadingSmall}
					>
						{t("OneAgreementPerCustomer")}
						<br />
						{t("AccessCanBeLimited")}
					</p>

					<div
						className="user-agreement-two-column-row"
						style={{ marginBottom: "20px" }}
					>
						<div
							className="user-agreement-column"
							style={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								gap: "10px",
							}}
						>
							{(form?.customerFisher || form?.customerSales) && (
								<>
									<div
										className={styles.BoxHeadingSmall}
										style={{ marginTop: 8, textAlign: "center" }}
									>
										{`${t("FishermanInfomation")}`}
									</div>
									<div
										className="user-agreement-grid"
										style={{
											display: "grid",
											gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
											gap: 12,
										}}
									>
										<CustomInput
											label={t("VesselRegistrationNo")}
											value={form?.vesselRegNo ?? ""}
											onChange={(v) => handleInputChange("vesselRegNo", v)}
											isError={!!errors.vesselRegNo}
											placeholder={t("VesselRegistrationNo")}
										/>
										<CustomInput
											label={t("VesselName")}
											value={form?.vesselName ?? ""}
											onChange={(v) => handleInputChange("vesselName", v)}
											isError={!!errors.vesselName}
											placeholder={t("VesselName")}
										/>
									</div>
								</>
							)}

							{(form?.customerBuyer || form?.customerSales) && (
								<>
									<div
										className={styles.BoxHeadingSmall}
										style={{ marginTop: 12, textAlign: "center" }}
									>
										{t("BuyerInformation")}
									</div>
									<div
										className="user-agreement-grid"
										style={{
											display: "grid",
											gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
											gap: 12,
										}}
									>
										<CustomDropDownList
											onSelect={(buyer) =>
												setForm((prev) => ({
													...(prev ?? defaultForm),
													buyerName: buyer.name,
													buyerRegNo: buyer.orgNo,
													buyerId: buyer.id,
												}))
											}
											data={[]}
											value={{
												id: form?.buyerRegNo || null,
												displayName: form?.buyerName || t("ChooseBuyer"),
											}}
											dataItemKey="id"
											textField="displayName"
											label={t("BuyerName")}
											defaultItem={{ id: null, displayName: t("ChooseBuyer") }}
											filterable
											serverFilter
											serverFilterApi={getBuyersFilterApi}
											showError={!!errors.buyerName}
										/>
										<CustomInput
											type="number"
											label={`${t("OrgNumber")}`}
											value={form?.buyerRegNo ?? ""}
											onChange={(v) => handleInputChange("buyerRegNo", v)}
											isError={!!errors.buyerRegNo}
											placeholder={`${t("OrgNumber")}`}
										/>
									</div>
								</>
							)}

							{form?.customerStorage && (
								<>
									<div
										className={styles.BoxHeadingSmall}
										style={{ marginTop: 12, textAlign: "center" }}
									>
										{`${t("TerminalInformation")}`}
									</div>
									<div
										className="user-agreement-grid"
										style={{
											display: "grid",
											gridTemplateColumns: isMobileView ? "1fr" : "1fr 1fr",
											gap: 12,
										}}
									>
										<CustomDropDownList
											onSelect={(terminal) =>
												setForm((prev) => ({
													...(prev ?? defaultForm),
													terminalCode: terminal?.code,
													terminalName: terminal?.name,
												}))
											}
											data={[]}
											value={{
												code: form?.terminalCode ?? "",
												displayName:
													form?.terminalName ||
													`${t("Choose")} ${t("Terminal")}`,
											}}
											itemRender={DropdownRenderItem}
											dataItemKey="code"
											textField="displayName"
											label={t("Terminal")}
											defaultItem={{
												code: null,
												displayName: `${t("Choose")} ${t("Terminal")}`,
											}}
											filterable
											serverFilter
											serverFilterApi={getTerminalsApi}
											showError={!!errors.terminalName}
										/>
									</div>
								</>
							)}
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>

				<div className="user-agreement-section">
					<div
						style={{
							display: "flex",
							alignItems: "center",
							gap: "20px",
							marginBottom: "20px",
						}}
					>
						<div className={styles.BoxHeadingMedium} style={{ margin: 0 }}>
							2. {`${t("User")}`}
						</div>
					</div>

					<div className="user-agreement-two-column-row">
						<div className="user-agreement-column">
							<div
								className="user-agreement-user-fields"
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "20px",
								}}
							>
								<div>
									<div style={{ display: "flex", gap: "10px" }}>
										<CustomInput
											label={t("CatchSign") + " " + t("Username")}
											placeholder={t("CatchSign") + " " + t("Username")}
											value={tempUsername}
											onChange={(value) => {
												if (
													value.trim().toUpperCase() !==
													tempUsername.trim().toUpperCase()
												) {
													setErrors((prev) => ({
														...prev,
														usernameInValid: "",
													}));
													invalidateUsername();
												}
												setTempUsername(value.trim().toUpperCase());
											}}
											inputStyle={{
												width: "100%",

												borderColor: errors.usernameInValid ? "red" : undefined,
											}}
										/>
										<div style={{ marginTop: "24px" }}>
											{usernameValid ? (
												<img
													src={`icon/${ICON_NAMES.CheckGreen}.svg`}
													alt={"username valid"}
													width={30}
													height={30}
												/>
											) : (
												<CustomButton
													label={t("Validate")}
													bgColor={COLOR.Green}
													color="white"
													onClick={validateUsername}
													disabled={!isValidUsername(tempUsername.trim())}
													btnStyle={
														isMobileView
															? { width: "100%" }
															: { height: "32px" }
													}
												/>
											)}
										</div>
									</div>
									{errors.usernameInValid && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.usernameInValid}
										</div>
									)}
								</div>

								<div>
									<CustomInput
										label={t("FirstName")}
										placeholder={t("FirstName")}
										value={form?.userFirstName ?? ""}
										readOnly={!usernameValid}
										onChange={(value) =>
											handleInputChange("userFirstName", value)
										}
										inputStyle={{
											width: "100%",
											borderColor: errors.userFirstName ? "red" : undefined,
										}}
									/>
									{errors.userFirstName && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.userFirstName}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("LastName")}
										placeholder={t("LastName")}
										value={form?.userLastName ?? ""}
										readOnly={!usernameValid}
										onChange={(value) =>
											handleInputChange("userLastName", value)
										}
										inputStyle={{
											width: "100%",
											borderColor: errors.userLastName ? "red" : undefined,
										}}
									/>
									{errors.userLastName && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.userLastName}
										</div>
									)}
								</div>
								<div>
									<div
										className="user-agreement-phone-row"
										style={{
											display: "flex",
											flexDirection: isMobileView ? "column" : "row",
											gap: "10px",
										}}
									>
										<div style={{ width: isMobileView ? "100%" : "50%" }}>
											<span style={{ paddingBottom: "4px", display: "block" }}>
												{t("CountryCode")}
											</span>
											<CustomDropDownList
												data={countryCodesFilter}
												//data={[]}
												dataItemKey="internationalDialing"
												textField="internationalDialing"
												filterable={true}
												// label={t("CountryCode")}
												// placeholder={t("CountryCode")}
												handleFilterChange={(e) => {
													if (e.filter) {
														handleFilterChange(e.filter.value);
													}
												}}
												value={{
													internationalDialing: form?.userPhonePrefix || "",
												}}
												// onSelect={(value) => {
												// 	// console.log(...value);
												// 	// handleInputChange(
												// 	// 	"userPhonePrefix",
												// 	// 	value.internationalDialing
												// 	// )
												// }}

												onChange={(e) => {
													handleInputChange(
														"userPhonePrefix",
														e.value.internationalDialing,
													);
													// console.log("changed", e);
												}}
												disabled={!usernameValid}
												defaultItem={{ internationalDialing: t("Choose") }}
												itemRender={(
													li: any, // React.ReactElement<HTMLLIElement>,
													itemProps: ListItemProps,
												) => {
													const data: InternationalCountryCode =
														itemProps.dataItem;

													const item = (
														<div
															style={{
																display: "flex",
																alignItems: "center",
																gap: "12px",
															}}
														>
															<span style={{ width: "50px" }}>
																{data.internationalDialing}
															</span>
															<span>{data.country}</span>
														</div>
													);

													return React.cloneElement(li, li.props, item);
												}}
												showError={!!errors.userPhonePrefix}
											/>
										</div>
										<div style={{ width: isMobileView ? "100%" : "50%" }}>
											<CustomInput
												label={t("PhoneNumber")}
												placeholder={t("PhoneNumber")}
												value={form?.userPhone ?? ""}
												type="number"
												readOnly={!usernameValid}
												onChange={(value) =>
													handleInputChange("userPhone", value)
												}
												inputStyle={{
													width: "100%",
													borderColor: errors.userPhone ? "red" : undefined,
												}}
											/>
										</div>
									</div>
									{errors.userPhone && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.userPhone}
										</div>
									)}
									{errors.userPhonePrefix && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.userPhonePrefix}
										</div>
									)}
								</div>
								<div>
									<CustomInput
										label={t("Email")}
										placeholder={t("Email")}
										value={form?.userEmail ?? ""}
										readOnly={!usernameValid}
										onChange={(value) => handleInputChange("userEmail", value)}
										inputStyle={{
											width: "100%",
											borderColor: errors.userEmail ? "red" : undefined,
										}}
									/>
									{errors.userEmail && (
										<div
											style={{
												color: "red",
												fontSize: "12px",
												marginTop: "4px",
											}}
										>
											{errors.userEmail}
										</div>
									)}
								</div>
								<CustomInput
									label={t("AccessToNumberOfDevices")}
									placeholder={t(" ")}
									type="number"
									readOnly={!usernameValid}
									value={form?.userDevices ?? ""}
									onChange={(value) => handleInputChange("userDevices", value)}
									inputStyle={{ width: "100%" }}
								/>
							</div>
						</div>
						<div className="user-agreement-column user-agreement-column-spacer"></div>
					</div>
				</div>

				<div
					className="user-agreement-terms-row"
					style={{
						marginTop: "30px",
						display: "flex",
						flexWrap: "wrap",
						alignItems: "center",
					}}
				>
					<Checkbox
						value={form?.termsOfUse ?? false}
						label={t("")}
						showError={showError && !form?.termsOfUse}
						onChange={() =>
							setForm((prev) => ({
								...(prev ?? defaultForm),
								termsOfUse: !(prev?.termsOfUse ?? false),
							}))
						}
					/>
					<div className="user-agreement-terms-copy">
						<span>{t("TermsAcceptancePre")}</span>
						<TextButton
							text={t("TermsAcceptanceLink")}
							textStyle={{
								color: COLOR.StatusBlue,
								textTransform: "lowercase",
							}}
							bgStyle={{ backgroundColor: "transparent" }}
							onClick={() => setTermsModalOpen(true)}
						/>
						<span>{t("TermsAcceptancePost")}</span>
					</div>
				</div>
				{showError && !form?.termsOfUse && (
					<div style={{ color: "red", fontSize: "12px", marginTop: "-15px" }}>
						{t("UserAgreement") + " " + t("MustBeAccepted")}
					</div>
				)}

				{termsModalOpen && (
					<CustomModal
						title={
							currentLanguage === "no" ? "Brukervilkår" : "Terms and Conditions"
						}
						style={{}}
						onClose={() => setTermsModalOpen(false)}
						actions={[
							{
								label: t("Close"),
								onClick: () => setTermsModalOpen(false),
							},
							{
								label: t("Accept"),
								bgColor: COLOR.Green,
								color: "white",
								onClick: () => {
									setForm((prev) => ({
										...(prev ?? defaultForm),
										termsOfUse: true,
									}));
									setTermsModalOpen(false);
								},
							},
						]}
					>
						{currentLanguage === "no" ? (
							<div
								className="user-agreement-modal-content"
								style={{ width: isMobileView ? "100%" : "600px" }}
							>
								<h4 style={{ marginTop: 0 }}>Kunden er ansvarlig for:</h4>
								<ul style={{ paddingLeft: 18 }}>
									<li>
										At brukeren benytter tilgangen bare til kundens eget formål
									</li>
									<li>De handler som bruker foretar i auksjonssystemet</li>
									<li>
										De sedler og salgsbekreftelser som bruker skriver/godkjenner
									</li>
									<li>
										Å melde fra umiddelbart til Surofi dersom bruker avslutter
										sitt arbeidsforhold og ikke lenger skal ha brukertilgang.
									</li>
									<li>
										At brukeren følger de regler som gjelder for bruk og
										oppbevaring av brukernavn og passord
									</li>
								</ul>
								<p>
									Brukernavn og passord er personlig og skal ikke formidles til
									eller deles med andre. Brukernavn og passord skal oppbevares
									slik at ikke andre har tilgang til disse. Surofi kan stenge
									tilgangen ved enhver form for misbruk eller mistanke om
									misbruk.
								</p>
							</div>
						) : (
							<div
								className="user-agreement-modal-content"
								style={{ width: isMobileView ? "100%" : "650px" }}
							>
								<h4 style={{ marginTop: 0 }}>
									The customer is responsible for:
								</h4>
								<ul style={{ paddingLeft: 18 }}>
									<li>
										The user using the access only for the customer’s own
										purposes
									</li>
									<li>The actions the user performs in the auction system</li>
									<li>
										The sales notes and confirmations the user issues/approves
									</li>
									<li>
										Immediately notifying Surofi if the user terminates
										employment and should no longer have access
									</li>
									<li>
										The user complying with the rules for the use and storage of
										username and password
									</li>
								</ul>
								<p>
									The username and password are personal and must not be shared
									or disclosed to others. They must be stored so that others
									cannot access them. Surofi may disable access in the event of
									any misuse or suspicion of misuse.
								</p>
							</div>
						)}
					</CustomModal>
				)}

				{successModalOpen && (
					<CustomModal
						title={t("Success", "Success")}
						onClose={() => setSuccessModalOpen(false)}
						actions={[
							{
								label: t("Close", "Close"),
								onClick: () => setSuccessModalOpen(false),
							},
						]}
					>
						<div
							className="user-agreement-success-content"
							style={{ width: isMobileView ? "100%" : 420 }}
						>
							{t(
								"AgreementRegisteredSuccessfully",
								"Agreement registered successfully.",
							)}
						</div>
					</CustomModal>
				)}

				<div
					className="user-agreement-actions-row"
					style={{
						marginTop: "40px",
						display: "flex",
						justifyContent: "center",
					}}
				>
					<CustomButton
						label={t("RegisterAgreement")}
						bgColor={COLOR.Green}
						color="white"
						disabled={!usernameValid}
						onClick={handleSubmit}
						btnStyle={isMobileView ? { width: "100%" } : undefined}
					/>
				</div>
			</div>
		</div>
	);
};

export default UserAgreementPage;
